import { useState, useEffect } from 'react'
import { X, Send, FileText } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { teacherApi } from '@/services/api/teacher'
import type { RadarBubble } from '@/services/api/teacher'
import { useAnimationConfig } from './hooks/useReducedMotion'
import { GlassButton } from './ui/GlassButton'

interface MessageModalProps {
  bubble: RadarBubble
  onClose: () => void
  onSuccess: () => void
}

/**
 * Message Modal - Glass Edition
 * 
 * Phase 5.5: Message Modal Glass Redesign
 */
export function MessageModal({ bubble, onClose, onSuccess }: MessageModalProps) {
  const animConfig = useAnimationConfig()
  
  const [message, setMessage] = useState('')
  const [includeDetails, setIncludeDetails] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [includeDeclined, setIncludeDeclined] = useState(true)
  const [includePending, setIncludePending] = useState(false)
  const [selectedLessonIds, setSelectedLessonIds] = useState<string[]>([])
  const [selectAllLessons, setSelectAllLessons] = useState(true)

  const allLessonIds = bubble.lessons?.map(l => l.lesson_request_id).filter((id): id is string => !!id) || []

  useEffect(() => {
    if (selectAllLessons) {
      setSelectedLessonIds(allLessonIds)
    }
  }, [selectAllLessons, allLessonIds])

  const handleLessonToggle = (lessonId: string) => {
    setSelectedLessonIds(prev => 
      prev.includes(lessonId) 
        ? prev.filter(id => id !== lessonId)
        : [...prev, lessonId]
    )
    setSelectAllLessons(false)
  }

  const handleSelectAllLessons = (checked: boolean) => {
    setSelectAllLessons(checked)
    if (checked) {
      setSelectedLessonIds(allLessonIds)
    } else {
      setSelectedLessonIds([])
    }
  }

  const handleSend = async () => {
    if (!message.trim()) return
    if (selectedLessonIds.length === 0) {
      setError('Keine Lektionen ausgewählt.')
      return
    }
    setSending(true)
    setError(null)
    try {
      const lessonRequestIds = selectedLessonIds
      const allIds: string[] = []
      if (includeDeclined && bubble.teachers_declined > 0) {
        const res = await teacherApi.getLessonTeachers(lessonRequestIds, 'declined')
        allIds.push(...res.teacher_ids)
      }
      if (includePending && bubble.teachers_pending > 0) {
        const res = await teacherApi.getLessonTeachers(lessonRequestIds, 'pending')
        allIds.push(...res.teacher_ids)
      }
      if (allIds.length === 0) {
        setError('Keine Empfänger ausgewählt.')
        return
      }
      await teacherApi.sendRadarMessage(lessonRequestIds, {
        recipient_teacher_ids: allIds,
        message_text: message,
        include_lesson_details: includeDetails,
      })
      onSuccess()
    } catch (e: any) {
      console.error('[MessageModal] Send error:', e)
      setError(e?.response?.data?.detail || 'Nachricht senden fehlgeschlagen')
    } finally {
      setSending(false)
    }
  }

  const charCount = message.length
  const maxChars = 500

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: animConfig.reducedMotion ? 0 : 0.2 }}
      >
        {/* Backdrop with blur */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        {/* Glass Modal */}
        <motion.div
          className="relative w-full max-w-lg rounded-2xl border"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            borderColor: 'rgba(255, 255, 255, 0.6)',
            boxShadow: `
              0 24px 48px rgba(0,0,0,0.15),
              0 12px 24px rgba(0,0,0,0.1),
              inset 0 1px 0 rgba(255,255,255,0.8)
            `,
          }}
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30,
            duration: animConfig.reducedMotion ? 0 : undefined,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Inner gradient for depth */}
          <div
            className="absolute inset-0 pointer-events-none rounded-2xl"
            style={{
              background: `
                linear-gradient(
                  180deg,
                  rgba(255, 255, 255, 0.5) 0%,
                  transparent 30%,
                  transparent 70%,
                  rgba(0, 0, 0, 0.02) 100%
                )
              `,
            }}
          />

          {/* Content */}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200/50">
              <h2 className="text-lg font-semibold text-gray-800">Nachricht senden</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/50 transition"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Lesson context */}
              <div 
                className="rounded-xl p-3 text-sm border"
                style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                }}
              >
                <div className="font-medium">{bubble.subject_name || '—'} — {bubble.klass_name || '—'}</div>
                <div className="text-gray-500">
                  {bubble.lesson_date ? new Date(bubble.lesson_date).toLocaleDateString('de-CH') : '—'}
                  {bubble.time_slot_name && `, ${bubble.time_slot_name}`}
                </div>
              </div>

              {/* Lesson selection */}
              {allLessonIds.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Lektionen</h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectAllLessons}
                        onChange={(e) => handleSelectAllLessons(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>Alle Lektionen ({allLessonIds.length})</span>
                    </label>
                    {!selectAllLessons && (
                      <div className="ml-6 space-y-1">
                        {bubble.lessons?.map((lesson) => (
                          <label key={lesson.lesson_request_id} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedLessonIds.includes(lesson.lesson_request_id || '')}
                              onChange={() => handleLessonToggle(lesson.lesson_request_id || '')}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>
                              {lesson.date ? new Date(lesson.date).toLocaleDateString('de-CH') : '—'}
                              {lesson.time_slot_name && `, ${lesson.time_slot_name}`}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recipient selection */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Empfänger</h3>
                <div className="space-y-2">
                  <label className={`flex items-center gap-2 text-sm ${bubble.teachers_declined === 0 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={includeDeclined}
                      onChange={(e) => setIncludeDeclined(e.target.checked)}
                      disabled={bubble.teachers_declined === 0}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500 disabled:opacity-50"
                    />
                    <span>Abgelehnte ({bubble.teachers_declined})</span>
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${bubble.teachers_pending === 0 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={includePending}
                      onChange={(e) => setIncludePending(e.target.checked)}
                      disabled={bubble.teachers_pending === 0}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:opacity-50"
                    />
                    <span>Ausstehende ({bubble.teachers_pending})</span>
                  </label>
                </div>
              </div>

              {/* Message */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Nachricht</h3>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ihre Nachricht..."
                  className="w-full p-3 border border-gray-300 rounded-xl text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  maxLength={maxChars}
                />
                <div className="flex items-center justify-between mt-1">
                  <span className={`text-xs ${charCount > maxChars * 0.9 ? 'text-red-500' : 'text-gray-400'}`}>
                    {charCount}/{maxChars}
                  </span>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeDetails}
                      onChange={(e) => setIncludeDetails(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <FileText className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-gray-600">Unterrichtsdetails anhängen</span>
                  </label>
                </div>
              </div>

              {/* Preview */}
              {includeDetails && (
                <div 
                  className="rounded-xl p-3 text-xs text-gray-600 border"
                  style={{
                    background: 'rgba(255, 255, 255, 0.5)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  }}
                >
                  <div className="font-medium text-gray-700 mb-1">Vorschau Unterrichtsdetails:</div>
                  <div>Datum: {bubble.lesson_date ? new Date(bubble.lesson_date).toLocaleDateString('de-CH') : '—'}</div>
                  <div>Lektion: {bubble.time_slot_name || '—'}</div>
                  <div>Fach: {bubble.subject_name || '—'}</div>
                  {bubble.klass_name && <div>Klasse: {bubble.klass_name}</div>}
                  {bubble.room_name && <div>Zimmer: {bubble.room_name}</div>}
                </div>
              )}

              {error && (
                <div className="p-2 bg-red-50 text-red-700 rounded text-sm">
                  {error}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-gray-200/50 flex gap-2">
              <GlassButton
                onClick={onClose}
                variant="secondary"
                className="flex-1"
              >
                Abbrechen
              </GlassButton>
              <GlassButton
                onClick={handleSend}
                disabled={sending || !message.trim() || (!includeDeclined && !includePending) || selectedLessonIds.length === 0}
                variant="primary"
                className="flex-1"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Senden...' : 'Senden'}
              </GlassButton>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
